import type { RegistrationId } from '~/domain/changes/ids';
import type { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    isKnownFor,
    sharesFrameOfReference,
    toFrameOfReferenceUid,
} from '~/domain/dicom/uids';
import type { Image3D } from './image3d';
import type { RigidRegistration } from './rigidRegistration';

/**
 * How a secondary image lies on a primary image's frame of reference: on
 * the same frame, or carried across by one rigid registration, with that
 * registration's approval status as it stands.
 */
export type ImageBinding =
    | { readonly kind: 'identity' }
    | {
          readonly kind: 'mapped';
          readonly registrationId: RegistrationId;
          readonly approvalStatus: DICOMApprovalStatus;
      };

type FrameImage = Pick<Image3D, 'frameOfReferenceUid'>;

/**
 * Every way `secondary` can be drawn on `primary`'s frame of reference. A
 * shared known frame gives one identity binding and needs no registration.
 * Otherwise each of `registrationsFrom` gives one mapped binding, in its
 * order; `registrationsFrom` are the registrations that place the
 * secondary's frame into the primary's, as `Patient.findRegistrationsFrom`
 * returns them. A missing frame of reference on either side gives none: it
 * shares nothing, and no registration places it.
 */
export function bindingsOf(
    primary: FrameImage,
    secondary: FrameImage,
    registrationsFrom: ReadonlyArray<
        Pick<RigidRegistration, 'id' | 'approvalStatus'>
    >
): ImageBinding[] {
    if (
        sharesFrameOfReference(
            primary.frameOfReferenceUid,
            secondary.frameOfReferenceUid
        )
    ) {
        return [{ kind: 'identity' }];
    }
    if (
        !isKnownFor(toFrameOfReferenceUid(primary.frameOfReferenceUid)) ||
        !isKnownFor(toFrameOfReferenceUid(secondary.frameOfReferenceUid))
    ) {
        return [];
    }
    return registrationsFrom.map((registration) => ({
        kind: 'mapped',
        registrationId: registration.id,
        approvalStatus: registration.approvalStatus,
    }));
}

/**
 * A key that tells two placements of an image apart: the image, and the
 * registration that places it or none for a shared frame. The approval a
 * binding carries is not part of it, so approving a registration leaves
 * the placement the same.
 */
export function placementKey(seriesUid: string, binding: ImageBinding): string {
    const through =
        binding.kind === 'mapped' ? binding.registrationId : 'identity';
    return `${seriesUid}|${through}`;
}
